<template>
  <li :class="`text-${labelColorClass}`">
    <a
      v-if="isHttp && isGet"
      class="accessibility-outline outline-inset"
      :class="`text-${labelColorClass}`"
      :href="route"
      role="menuitem"
      :aria-label="label"
      :aria-description="isDownload ? 'Lädt die Datei herunter' : 'Führt die Aktion aus'"
    >{{ label }}</a>
    <a
      v-else-if="isDownload"
      class="accessibility-outline outline-inset"
      :class="`text-${labelColorClass}`"
      :href="route"
      download
      role="menuitem"
      :aria-label="label"
      aria-description="Lädt die Datei herunter"
    >{{ label }}</a>
    <span
      v-else
      class="accessibility-outline outline-inset"
      :class="`text-${labelColorClass}`"
      role="menuitem"
      tabindex="0"
      :aria-label="label"
      :aria-description="isDownload ? 'Lädt die Datei herunter' : 'Führt die Aktion aus'"
      @click="getAction"
      @keydown.enter.prevent="getAction"
      @keydown.space.prevent="getAction"
    >{{ label }}</span>
  </li>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { defineProps, computed, inject } from 'vue';

import { responseHandler } from '@/helper';
import { isObject, has } from '@/helper/Object';

import ModalDataInterface from '@/interfaces/ModalDataInterface';

import { useBaseModalStore } from '@/stores/modal';

const props = defineProps({
  route: {
    type: String,
    default: '',
  },
  method: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  action: {
    type: String,
    default: 'post',
  },
  target: {
    type: [String, HTMLElement, Object],
    required: true,
  },
  needsConfirmation: {
    type: Boolean,
    default: false,
    required: false,
  },
  confirmationText: {
    type: String,
    default: '',
  },
  confirmationTitle: {
    type: String,
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  labelColorClass: {
    type: String,
    default: 'body',
  },
  update: {
    type: Object,
    default: () => ({}),
  },
  data: {
    type: [Object as () => Record<string, any>, Array as () => Record<string, any>[]],
    default: () => ({}),
  },
});

defineEmits(['action', 'clear', 'click']);

// eslint-disable-next-line vue/no-setup-props-destructure
const targetEl = props.target;

const isHttp = computed(() => `${props.type || ''}`.toLowerCase() === 'http');
const isDownload = computed(() => `${props.type || ''}`.toLowerCase() === 'download');
const isModal = computed(() => `${props.type || ''}`.toLowerCase() === 'modal');
const isGet = computed(() => `${props.action || ''}`.toLowerCase() === 'get');

const vuexStore = inject('vuex-store') as { _mutations: Record<string, ((...params: any[]) => any)[]>};
// eslint-disable-next-line no-underscore-dangle
const setFormDefaults = vuexStore._mutations['baseForm/SET_DEFAULTS'][0];
// eslint-disable-next-line no-underscore-dangle
const setModalProps = vuexStore._mutations['baseModal/SET_PROPS'][0];
// eslint-disable-next-line no-underscore-dangle
const setModalTitle = vuexStore._mutations['baseModal/SET_TITLE'][0];
// eslint-disable-next-line no-underscore-dangle
const setModalComponent = vuexStore._mutations['baseModal/SET_COMPONENT'][0];

const baseModalStore = useBaseModalStore();

const getAction = () => {
  if (isModal.value && props.method === 'template') {
    const { data } = props;
    // @ts-ignore
    const { store, title, component } = data;
    if (isObject(data) && has(data, 'store', 'title', 'component') && store === 'pinia') {
      baseModalStore.setProps(data);
      baseModalStore.setTitle(title);
      baseModalStore.setComponent(component);
    } else if (isObject(data) && has(data, 'event')) {
      window.GlobalEvents.fire('context-menu-action', {
        target: targetEl,
        data: {
          ...props.data,
        },
      });
    } else if (isObject(data) && has(data, 'title', 'component')) {
      setModalProps(data);
      setModalTitle(title);
      setModalComponent(component);
    }
  } else if (isGet.value) {
    if (props.route) {
      axios.get(props.route)
        .then((res) => {
          if (isModal.value) {
            window.GlobalEvents.fire('context-menu-action', {
              target: targetEl,
              data: {
                type: 'modal',
                action: props.action,
                method: props.method,
                update: props.update ? props.update : '',
                data: res.data, // contains form data if user clicks edit
              },
            });
            setFormDefaults(res.data);
          }
          // don't show message for fetching data
          responseHandler(res, false);
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      window.GlobalEvents.fire('context-menu-action', {
        target: targetEl,
        data: {
          type: 'modal',
          method: props.method,
          update: props.update ? props.update : '',
        },
      });
    }
  } else if (props.needsConfirmation) {
    window.GlobalEvents.fire('context-menu-clear', {
      target: targetEl,
      data: {
        type: 'modal',
        method: props.method,
        clear: props.route,
        title: props.confirmationTitle,
        action: props.action,
        confirmationText: props.confirmationText,
      },
    });
  } else if (isModal.value) {
    window.GlobalEvents.fire('context-menu-action', {
      target: targetEl,
      data: {
        type: props.type,
        method: props.method,
        action: props.action,
        data: props.data || {},
      },
    });
  } else {
    axios.post(props.route)
      .then((res) => {
        const message = res.data.message || res.data.messages;
        window.GlobalEvents.fire('alert', [{ message, type: 'success' }]);
        // @ts-ignore
        window.GlobalEvents.fire('update-data-table');
      })
      .catch((err) => {
        const message = err.response.data.message || err.response.data.messages;
        window.GlobalEvents.fire('alert', [{ message, type: 'error' }]);
      });
  }
};
</script>
